import { useState } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../auth/msalConfig";
import { CoordinateOverlay, type Marker, type LabelBox } from "./CoordinateOverlay";
import { theme, tint } from "../theme";

export interface DetectionMarker {
  x:              number;
  y:              number;
  color:          string;
  label:          string;
  score:          number | null;
  is_first_image: boolean;
  w?:             number;   // optional box size (0-1) from the cloud model; both set → drawn as a box
  h?:             number;
}

// The router's ORIGINAL edge-model markers (no server-side colour — derived
// per label on the client so Edge and Cloud layers match for the same label).
export interface EdgeMarker {
  x:              number;
  y:              number;
  label:          string;
  score:          number | null;
  is_first_image: boolean;
}

export interface DetectionEvent {
  type_name: string;
  bbox_x:    number;
  bbox_y:    number;
}

export interface Detection {
  id:               number;
  device_serial:    string | null;
  timestamp:        string;
  detection_events: DetectionEvent[];
  confidence_score: number;
  image_before_url: string | null;
  image_after_url:  string | null;
  evaluation:       string | null;   // human verdict (real / false_alarm)
  ml_verdict:       string | null;   // central-ML verdict (real/false_alarm/error/null)
  // Why the detection was withheld, or null. "no_change" = its own before/after
  // images show the same scene; "previous" = it repeats the immediately
  // preceding detection; "too_dark" = at least one image is too dim to review.
  duplicate_reason: string | null;
  // Per-frame manual-relabeling state: null (not asked for), "requested"
  // (waiting in the queue) or "sent" (already in a review task, so final).
  relabel_before:   string | null;
  relabel_after:    string | null;
  markers:          DetectionMarker[];   // published central-model output
  edge_markers:     EdgeMarker[];         // "Edge labeled" — router's original markers
  edge_events:      DetectionEvent[];     // router's original event circles
  downloaded_at:    string | null;
  // Historic gpt_* fields remain in the API for database compatibility.
  gpt_label_status: string | null;
  gpt_labels:       GptLabel[] | null;
  central_label_status: string | null;
  central_labels:       GptLabel[] | null;
  cvat_task_id:     number | null;   // same task's id — used for the in-app /review editor link
}

export interface GptLabel {
  label: string;
  box:   [number, number, number, number];  // cx, cy, w, h (0-1)
  image: "before" | "after";
}

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

// ── Category classification ────────────────────────────────────────────────────
// "helmet" in a type name → Safety incident; anything else → Disappearance
interface CategoryCfg {
  category: string;   // badge label
  label: string;      // e.g. "Cause" / "Missing"
  value: string;      // the type name
  color: string;
}

function categorizeType(typeName: string): CategoryCfg {
  if (typeName.toLowerCase().includes("helmet")) {
    return { category: "Safety",        label: "Cause",   value: typeName, color: theme.warning };
  }
  return   { category: "Disappearance", label: "Missing", value: typeName, color: theme.danger  };
}

/** Renders "CAUSE  helmet" / "MISSING  sign" with visually distinct label vs value. */
function Subtitle({ label, value, large = false }: { label: string; value: string; large?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: large ? 6 : 5 }}>
      <span style={{
        fontSize: large ? 11 : 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: theme.textMuted,
      }}>
        {label}:
      </span>
      <span style={{
        fontSize: large ? 13 : 12,
        fontWeight: 600,
        color: theme.textPrimary,
        letterSpacing: "0.01em",
      }}>
        {value}
      </span>
    </span>
  );
}

const EVAL_CONFIG = {
  // Green confirms, red dismisses. The pair used to run the other way round —
  // red for "Real event", green for "False alarm" — which reads as the severity
  // of the incident rather than as the verdict. But these are the reviewer's own
  // two choices, and on a pair of buttons the colour is taken as approve/reject:
  // clicking green to file something as a false alarm invites the wrong click.
  real:        { label: "Real event",   bg: theme.success, text: theme.onDark },
  false_alarm: { label: "False alarm",  bg: theme.danger,  text: theme.onDark },
} as const;

type LabelView = "edge" | "cloud" | "central";

const LABEL_VIEW_TITLE: Record<LabelView, string> = {
  edge:  "Edge labeled",
  cloud: "Central model output",
  central: "Central model labels",
};

// Palette names understood by CoordinateOverlay's DOT_COLORS.
const MARKER_PALETTE = ["green", "blue", "orange", "red", "purple"] as const;

/** Deterministic palette name for a label, used to colour edge markers whose
 *  label isn't among the cloud markers (which carry a server-assigned colour).
 *  Same label → same colour across cards. */
function paletteForLabel(label: string): string {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return MARKER_PALETTE[h % MARKER_PALETTE.length];
}

interface Props {
  detection: Detection;
  showMarkers?: boolean;
  onEvaluationChange?: (id: number, evaluation: string | null) => void;
  onDelete?: (id: number) => void;
}

export function DetectionCard({ detection, showMarkers = true, onEvaluationChange, onDelete }: Props) {
  const { instance, accounts } = useMsal();
  const [modalOpen, setModalOpen] = useState(false);
  // Three labeling layers:
  //   "cloud" = central model markers/circles (the published view)
  //   "edge"  = the router's own original markers/circles
  //   "central" = direct boxes generated by the active central model
  const [modalView, setModalView] = useState<LabelView>("cloud");
  const [evaluation, setEvaluation] = useState<string | null>(detection.evaluation);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Local markers toggle — initialises from the page-level prop but can be
  // overridden independently inside the modal.
  const [localShowMarkers, setLocalShowMarkers] = useState(showMarkers);

  const ts = new Date(detection.timestamp).toLocaleString("en-GB");
  const confidence = Math.round(detection.confidence_score * 100);
  // Build one CategoryCfg per event; fall back to a single "Unknown" entry if empty
  const catCfgs: CategoryCfg[] = detection.detection_events?.length
    ? detection.detection_events.map((e) => categorizeType(e.type_name))
    : [{ category: "Unknown", label: "Type", value: "—", color: theme.textMuted }];
  // Published central-model markers — a proper array from the API.
  // Pass optional w/h through so markers that carry both render as boxes.
  const markers: Marker[] = (detection.markers ?? []).map((m) => ({
    x: m.x, y: m.y, color: m.color, label: m.label, score: m.score,
    is_first_image: m.is_first_image, w: m.w, h: m.h,
  }));

  // "Edge labeled" markers (the router's originals). No server-side colour;
  // reuse the cloud colour for the same label so the two layers are visually
  // comparable, falling back to a deterministic palette colour otherwise.
  const labelColor = new Map<string, string>();
  for (const m of markers) labelColor.set(m.label, m.color);
  const edgeMarkers: Marker[] = (detection.edge_markers ?? []).map((m) => ({
    x: m.x, y: m.y, label: m.label, score: m.score, is_first_image: m.is_first_image,
    color: labelColor.get(m.label) ?? paletteForLabel(m.label),
  }));

  // ── Unique category badges (deduplicated) ──────────────────────────────────
  const uniqueCatCfgs: CategoryCfg[] = catCfgs.filter(
    (c, i, arr) => arr.findIndex((u) => u.category === c.category) === i
  );

  // ── Central-ML verdict badge (distinct from the human evaluation below) ─────
  // Hex literals (NOT theme CSS-vars) so the `tint(color, 53)` alpha border below
  // produces a valid colour.
  const mlBadge =
    detection.ml_verdict === "false_alarm"
      ? { label: "AI false-positive", color: theme.info }
      : detection.ml_verdict === "error"
      ? { label: "AI eval error", color: theme.warning }
      : null;

  // ── Red circle logic ───────────────────────────────────────────────────────
  // Coordinates come directly from detection_events (bbox_x/bbox_y per event),
  // annotated by the router in the bounding_boxes JSON before upload.
  // Disappearance events → BEFORE image; Safety events → AFTER image.
  const circlesFrom = (events: DetectionEvent[], category: "Disappearance" | "Safety") =>
    events.filter((e) => categorizeType(e.type_name).category === category)
          .map((e) => ({ x: e.bbox_x, y: e.bbox_y }));
  const beforeCircles = circlesFrom(detection.detection_events ?? [], "Disappearance");
  const afterCircles  = circlesFrom(detection.detection_events ?? [], "Safety");
  const edgeBeforeCircles = circlesFrom(detection.edge_events ?? [], "Disappearance");
  const edgeAfterCircles  = circlesFrom(detection.edge_events ?? [], "Safety");

  // ── Direct central-model boxes, split per image ─────────────────────────────
  // Colour each box per label — same mapping as the published markers — so the
  // Central-model view is not one flat colour.
  const centralLabels = detection.central_labels ?? detection.gpt_labels ?? [];
  const centralStatus = detection.central_label_status ?? detection.gpt_label_status;
  const toBox = (g: GptLabel): LabelBox =>
    ({ x: g.box[0], y: g.box[1], w: g.box[2], h: g.box[3], label: g.label,
       color: labelColor.get(g.label) ?? paletteForLabel(g.label) });
  const centralBefore = centralLabels.filter((g) => g.image === "before").map(toBox);
  const centralAfter  = centralLabels.filter((g) => g.image === "after").map(toBox);
  const hasCentral     = centralStatus === "done" && centralLabels.length > 0;
  const labelPending = centralStatus === "pending";
  const labelQueued  = centralStatus === "queued";
  const centralView  = modalView === "central";

  // Which labeling layers this detection actually has data for (Cloud is
  // always offered — it's the published view). Only render the toggle when
  // there's more than one to switch between.
  const availableViews: LabelView[] = [
    ...(edgeMarkers.length ? (["edge"] as const) : []),
    "cloud" as const,
    ...(hasCentral ? (["central"] as const) : []),
  ];

  // Per-view overlay inputs, so the two <CoordinateOverlay>s below stay in sync.
  const viewMarkers = modalView === "edge" ? edgeMarkers : modalView === "cloud" ? markers : [];
  const viewBeforeCircles = modalView === "edge" ? edgeBeforeCircles : modalView === "cloud" ? beforeCircles : [];
  const viewAfterCircles  = modalView === "edge" ? edgeAfterCircles  : modalView === "cloud" ? afterCircles  : [];

  const openModal = (view: LabelView) => { setModalView(view); setModalOpen(true); };

  const handleEvaluation = async (value: "real" | "false_alarm") => {
    const next = evaluation === value ? null : value;
    setSaving(true);
    try {
      const result = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/detections/${detection.id}/evaluation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${result.accessToken}` },
        body: JSON.stringify({ evaluation: next }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEvaluation(next);
      onEvaluationChange?.(detection.id, next);
    } catch (err) {
      console.error("Evaluation save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Per-frame relabeling requests ──────────────────────────────────────────
  // Held locally as well as on the row so the button reacts to the click rather
  // than to a round trip; the server stays the authority and a failure puts the
  // optimistic value back.
  const [relabel, setRelabel] = useState<{ before: string | null; after: string | null }>({
    before: detection.relabel_before ?? null,
    after:  detection.relabel_after ?? null,
  });
  const [relabelBusy, setRelabelBusy] = useState(false);

  const relabelState = (isAfter: boolean) => (isAfter ? relabel.after : relabel.before);

  const handleRelabel = async (tag: "before" | "after") => {
    const current = tag === "after" ? relabel.after : relabel.before;
    if (current === "sent") return;                 // a task cannot be un-sent
    const requested = current !== "requested";      // click toggles
    const previous = relabel;
    setRelabel((r) => ({ ...r, [tag]: requested ? "requested" : null }));
    setRelabelBusy(true);
    try {
      const result = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/detections/${detection.id}/relabel-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${result.accessToken}` },
        body: JSON.stringify({ image: tag, requested }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const updated = await resp.json();
      setRelabel({ before: updated.relabel_before ?? null, after: updated.relabel_after ?? null });
      onEvaluationChange?.(detection.id, evaluation);  // refresh the queue badge
    } catch (err) {
      console.error("Relabel request failed:", err);
      setRelabel(previous);
    } finally {
      setRelabelBusy(false);
    }
  };

  // One definition, used by both the grid card and the enlarged modal — the two
  // showed the same frames and would have drifted apart as separate copies.
  const relabelChip = (isAfter: boolean, large = false) => {
    const state = relabelState(isAfter);
    const done  = state === "sent";
    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleRelabel(isAfter ? "after" : "before"); }}
        disabled={relabelBusy || done}
        title={
          done
            ? "Already sent to an annotator — a task cannot be un-sent"
            : state === "requested"
            ? "Queued for a human to redraw. Click to take it back."
            : "The boxes on this frame are wrong. Click to queue this one frame for a human to redraw."
        }
        style={{
          position: "absolute", bottom: large ? 10 : 7, right: large ? 10 : 7,
          display: "inline-flex", alignItems: "center", gap: large ? 6 : 5,
          padding: large ? "6px 12px" : "4px 9px",
          borderRadius: large ? 16 : 13,
          border: `1px solid ${state ? tint(theme.warning, 85) : "rgba(255,255,255,0.4)"}`,
          background: state ? theme.warning : "rgba(26,28,30,0.7)",
          color: state ? theme.onGold : theme.onDark,
          fontFamily: "inherit", fontSize: large ? 13 : 11, fontWeight: 700,
          letterSpacing: "0.01em", whiteSpace: "nowrap",
          cursor: relabelBusy || done ? "default" : "pointer",
          opacity: relabelBusy ? 0.6 : 1,
        }}
      >
        <svg width={large ? 14 : 12} height={large ? 14 : 12} viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {done
            ? <polyline points="20 6 9 17 4 12" />
            : state === "requested"
            ? <><path d="M3 12a9 9 0 1 0 9-9" /><polyline points="3 4 3 12 11 12" /></>
            : <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></>}
        </svg>
        {done ? "Sent to annotator" : state === "requested" ? "Click to undo" : "Click to fix labels"}
      </button>
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this detection event?")) return;
    setDeleting(true);
    try {
      const result = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/detections/${detection.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${result.accessToken}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      onDelete?.(detection.id);
    } catch (err) {
      console.error("Delete failed:", err);
      setDeleting(false);
    }
  };

  const evalButtons = (large = false) => (
    <div style={{ display: "flex", gap: large ? 10 : 6 }}>
      {(["real", "false_alarm"] as const).map((val) => {
        const cfg = EVAL_CONFIG[val];
        const active = evaluation === val;
        return (
                    <button
            key={val}
            disabled={saving}
            onClick={(e) => { e.stopPropagation(); handleEvaluation(val); }}
            style={{
              flex: 1,
              padding: large ? "8px 20px" : "5px 0",
              fontSize: large ? 14 : 12,
              fontWeight: 600,
              borderRadius: theme.radius,
              border: `1.5px solid ${active ? cfg.bg : theme.border}`,
              background: active ? cfg.bg : "transparent",
              color: active ? cfg.text : theme.textSecondary,
              cursor: saving ? "not-allowed" : "pointer",
              transition: "all 0.15s",
              letterSpacing: "0.02em",
            }}
          >
            {cfg.label}
          </button>
        );
      })}
      {large && evaluation && (
        <button
          disabled={saving}
          onClick={() => handleEvaluation(evaluation as "real" | "false_alarm")}
          style={{
            padding: "8px 14px",
            fontSize: 13,
            borderRadius: theme.radius,
            border: `1px solid ${theme.border}`,
            background: "transparent",
            color: theme.textMuted,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      )}
    </div>
  );

  // ── Category badges (one per unique category) ──
  const categoryBadges = (large = false) => (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {uniqueCatCfgs.map((cfg, i) => (
        <span
          key={i}
          style={{
            background: tint(cfg.color, 13),
            color: cfg.color,
            border: `1px solid ${tint(cfg.color, 33)}`,
            borderRadius: 4,
            padding: large ? "3px 10px" : "2px 8px",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {cfg.category}
        </span>
      ))}
    </div>
  );

  return (
    <>
      <div
        style={{
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          overflow: "hidden",
          background: theme.bgCard,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          // Both hidden-by-default categories read as "already accounted for"
          // when they are shown: still legible, but visibly not asking for a
          // decision, so a genuine event never has to compete with them for
          // attention on a mixed page.
          opacity: (detection.ml_verdict === "false_alarm" || detection.duplicate_reason) ? 0.68 : 1,
          transition: "box-shadow 0.15s, border-color 0.15s, opacity 0.15s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = tint(theme.gold, 53);
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = theme.border;
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
        }}
      >
        {/* Before / After thumbnails */}
        <div style={{ display: "flex", cursor: "pointer" }} onClick={() => openModal("cloud")}>
          {([
            { url: detection.image_before_url, label: "Before", isAfter: false },
            { url: detection.image_after_url,  label: "After",  isAfter: true  },
          ] as const).map(({ url, label, isAfter }) => (
            <div key={label} style={{ flex: 1, position: "relative" }}>
              {url ? (
                <img
                  src={url}
                  alt={label}
                  // Square centre-crop so the display matches the model's input
                  // (the classifier centre-crops to a square); this also aligns
                  // the 0-1 markers/circles, which are normalised to that square.
                  style={{ width: "100%", aspectRatio: "1", objectFit: "cover", objectPosition: "center", display: "block" }}
                  loading="lazy"
                />
              ) : (
                <div style={{
                  width: "100%", aspectRatio: "1", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  background: theme.bgBase, flexDirection: "column", gap: 6,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.textMuted, opacity: 0.5 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span style={{ fontSize: 10, color: theme.textMuted, letterSpacing: "0.04em" }}>Uploading…</span>
                </div>
              )}
              <CoordinateOverlay
                detectionCircles={showMarkers ? (isAfter ? afterCircles : beforeCircles) : []}
                markers={showMarkers ? markers : []}
                imageTag={isAfter ? "after" : "before"}
              />
              {/* Before / After label — white text on dark overlay */}
              <span
                style={{
                  position: "absolute",
                  top: 5,
                  left: 7,
                  background: "rgba(0,0,0,0.55)",
                  color: theme.onDark,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 3,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </span>

              {/* Flagging is per FRAME, and deliberately separate from the
                  verdict below. "This was not a real event" and "the model drew
                  the wrong boxes here" are different judgements: a detection can
                  be a false alarm with perfectly good boxes, and a real event
                  can be badly boxed. Only the second is worth an annotator's
                  time, and only on the frame that is actually wrong.

                  Corner-anchored and only as wide as its text: a full-width bar
                  read as a caption belonging to the picture rather than as
                  something to press, and it covered the bottom of the frame the
                  operator is trying to judge. */}
              {url && relabelChip(isAfter)}
            </div>
          ))}
        </div>

        {/* Meta */}
        <div style={{ padding: "10px 12px 12px" }}>
          {/* Row 1: category badges + confidence + delete */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              {categoryBadges()}
              {mlBadge && (
                <span
                  title={
                    detection.ml_verdict === "false_alarm"
                      ? "The central AI model found no anomaly — flagged as a false positive (different from a manual evaluation)"
                      : "The central AI model could not evaluate this detection; the router's own data is shown"
                  }
                  style={{
                    background: "transparent",
                    color: mlBadge.color,
                    border: `1px dashed ${tint(mlBadge.color, 53)}`,
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {mlBadge.label}
                </span>
              )}
              {detection.duplicate_reason && (
                <span
                  title={
                    detection.duplicate_reason === "too_dark"
                      ? "At least one image is below the brightness threshold and cannot be reviewed reliably"
                      : detection.duplicate_reason === "no_change"
                        ? "This detection's own before and after images show the same scene — the reported change is not visible in its own evidence"
                        : "Same scene as the previous detection from this device — grouped as a duplicate"
                  }
                  style={{
                    background: "transparent",
                    color: theme.textMuted,
                    // Dashed, muted and unfilled: a duplicate is a statement
                    // about redundancy, not a verdict on the scene, and it must
                    // not compete with the category badge beside it.
                    border: `1px dashed ${theme.borderStrong}`,
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {detection.duplicate_reason === "too_dark" ? "Too dark" : "Duplicate"}
                </span>
              )}
              {labelQueued && (
                <span
                  title="Labeling service is starting up — will label automatically once ready"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "#E0A12E18",
                    color: "#7C5F25",
                    border: "1px dashed #E0A12E88",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  ⏳ Queued
                </span>
              )}
              {labelPending && (
                <span
                    title="Running the active central model…"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "#A8A6A418",
                    color: "#5F5C5E",
                    border: "1px dashed #A8A6A488",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 0.9s linear infinite" }}>
                    <path d="M21 12a9 9 0 1 1-9-9" />
                  </svg>
                  Labeling…
                </span>
              )}
              {hasCentral && (
                <button
                  onClick={(e) => { e.stopPropagation(); openModal("central"); }}
                  title="Pre-labeled by the active central model — click to compare"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "#D8B06218",
                    color: "#7C5F25",
                    border: "1px solid #D8B06288",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l1.9 4.8L19 9l-4.1 1.2L12 15l-1.9-4.8L6 9l5.1-1.2z"/>
                  </svg>
                  Central model labels
                </button>
              )}
              {detection.cvat_task_id != null && (
                <Link
                  to={`/review/${detection.cvat_task_id}`}
                  onClick={(e) => e.stopPropagation()}
                  title="Open this item's review page — already sent for review"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: tint(theme.gold, 9),
                    color: theme.gold,
                    border: `1px solid ${tint(theme.gold, 53)}`,
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                >
                  Reviewed
                </Link>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {detection.downloaded_at && (
                <span
                  title={`Downloaded ${new Date(detection.downloaded_at).toLocaleString("en-GB")}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 3,
                    fontSize: 10, fontWeight: 700, color: theme.success,
                    border: `1px solid ${tint(theme.success, 33)}`,
                    background: tint(theme.success, 10),
                    borderRadius: 3, padding: "1px 5px",
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  DL
                </span>
              )}
              <span style={{ fontSize: 12, color: theme.gold, fontWeight: 600 }}>{confidence}%</span>
              <button
                title="Delete event"
                disabled={deleting}
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: deleting ? "not-allowed" : "pointer",
                  padding: "2px 4px",
                  color: theme.textMuted,
                  opacity: deleting ? 0.4 : 0.7,
                  lineHeight: 1,
                  transition: "opacity 0.15s, color 0.15s",
                  display: "flex",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => { if (!deleting) (e.currentTarget as HTMLButtonElement).style.color = theme.danger; (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = theme.textMuted; (e.currentTarget as HTMLButtonElement).style.opacity = "0.7"; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          </div>
          {/* Row 2: one subtitle per type */}
          {catCfgs.map((cfg, i) => (
            <p key={i} style={{ margin: "0 0 2px" }}>
              <Subtitle label={cfg.label} value={cfg.value} />
            </p>
          ))}
          {/* Row 3: timestamp */}
          <p style={{ margin: "0 0 8px", fontSize: 12, color: theme.textMuted }}>{ts}</p>
          {evalButtons(false)}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            // A scrim that dims rather than blacks out, plus a blur on what is
            // behind it. At 0.85 opaque the page underneath was simply gone, so
            // the dialog read as a new screen instead of as something opened on
            // top of the list you were working through.
            background: "rgba(26,28,30,0.62)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme.bgCard,
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: 24,
              // Nearly fills the viewport: the whole point of opening a
              // detection large is to inspect the boxes, and a 1040px cap left
              // most of a wide screen unused. Scrolls internally rather than
              // growing past the viewport on short windows.
              width: "min(1600px, 96vw)",
              maxHeight: "94vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                {categoryBadges(true)}
                {catCfgs.map((cfg, i) => (
                  <Subtitle key={i} label={cfg.label} value={cfg.value} large />
                ))}
                <span style={{ color: theme.textMuted, fontSize: 13 }}>{ts}</span>
                <span style={{ color: theme.gold, fontSize: 13, fontWeight: 600 }}>
                  Confidence: {confidence}%
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                {/* Edge / Cloud / central-model labeling-layer toggle (only the layers
                    this detection actually has; needs at least two to show) */}
                {availableViews.length > 1 && (
                  <div style={{ display: "inline-flex", border: `1px solid ${theme.border}`, borderRadius: theme.radius, overflow: "hidden" }}>
                    {availableViews.map((v) => (
                      <button
                        key={v}
                        onClick={() => setModalView(v)}
                        style={{
                          padding: "4px 12px",
                          fontSize: 13, fontWeight: 600, cursor: "pointer",
                          border: "none",
                          background: modalView === v ? "#D8B06222" : "transparent",
                          color: modalView === v ? "#7C5F25" : theme.textMuted,
                        }}
                      >
                        {LABEL_VIEW_TITLE[v]}
                      </button>
                    ))}
                  </div>
                )}
                {/* Markers toggle */}
                <button
                  onClick={() => setLocalShowMarkers((v) => !v)}
                  title={localShowMarkers ? "Hide markers" : "Show markers"}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "4px 12px",
                    borderRadius: theme.radius,
                    border: `1.5px solid ${localShowMarkers ? tint(theme.gold, 53) : theme.border}`,
                    background: localShowMarkers ? tint(theme.gold, 9) : "transparent",
                    color: localShowMarkers ? theme.gold : theme.textMuted,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {localShowMarkers
                      ? <><circle cx="12" cy="12" r="4"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/></>
                      : <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                    }
                  </svg>
                  Markers
                </button>
                {/* Close */}
                <button
                  onClick={() => setModalOpen(false)}
                  style={{
                    background: "transparent",
                    border: `1px solid ${theme.border}`,
                    color: theme.textSecondary,
                    borderRadius: theme.radius,
                    padding: "3px 12px",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Images */}
            <div style={{ display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 7px", color: theme.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>BEFORE</p>
                <div style={{ position: "relative" }}>
                  {detection.image_before_url
                    ? <img src={detection.image_before_url} alt="Before" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", objectPosition: "center", borderRadius: 6, display: "block" }} />
                    : <div style={{ width: "100%", aspectRatio: "1", background: theme.bgBase, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: theme.textMuted, fontSize: 12 }}>Uploading…</span></div>
                  }
                  <CoordinateOverlay
                    detectionCircles={localShowMarkers ? viewBeforeCircles : []}
                    markers={localShowMarkers ? viewMarkers : []}
                    boxes={centralView ? centralBefore : []}
                    imageTag="before"
                    anchor={modalView === "edge" ? "corner" : "center"}
                  />
                  {detection.image_before_url && relabelChip(false, true)}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 7px", color: theme.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>AFTER</p>
                <div style={{ position: "relative" }}>
                  {detection.image_after_url
                    ? <img src={detection.image_after_url} alt="After" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", objectPosition: "center", borderRadius: 6, display: "block" }} />
                    : <div style={{ width: "100%", aspectRatio: "1", background: theme.bgBase, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: theme.textMuted, fontSize: 12 }}>Uploading…</span></div>
                  }
                  <CoordinateOverlay
                    detectionCircles={localShowMarkers ? viewAfterCircles : []}
                    markers={localShowMarkers ? viewMarkers : []}
                    boxes={centralView ? centralAfter : []}
                    imageTag="after"
                    anchor={modalView === "edge" ? "corner" : "center"}
                  />
                  {detection.image_after_url && relabelChip(true, true)}
                </div>
              </div>
            </div>

            {/* Evaluation */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
              <p style={{ margin: "0 0 10px", color: theme.textSecondary, fontSize: 13, fontWeight: 600, letterSpacing: "0.03em" }}>
                Evaluation
              </p>
              {evalButtons(true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
